import { ContentCopyRounded } from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogContent,
	Grid,
	IconButton,
	LinearProgress,
	TextField,
	Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { Controller, useForm } from "react-hook-form";
import { showToast } from "../../utils/toast";
import validator from "validator";
import { axiosInstance } from "../../config/axios";
import { usePlayoffContext } from "../../context/PlayoffContext";
import { FullHeightLoader } from "../Loader/FullHeightLoader";
import { StatusAvatar } from "./StatusAvatar";

export const UploadSheetDrawer = ({
	open,
	setOpen,
	group,
	setProducts,
	setUniqueProducts,
}) => {
	const { control, handleSubmit, reset } = useForm({});
	const [hideForm, setHideForm] = useState(false);
	const [loading, setLoading] = useState(false);
	const [tasks, setTasks] = useState([]);
	const tasksRef = useRef(tasks);
	const taskIds = tasks.map((task) => task.id).join(",");
	const toggle = () => {
		reset({});
		setTasks([]);
		setHideForm(false);
		setOpen(!open);
	};
	const statusColorMap = {
		success: "success",
		error: "error",
		processing: "info",
		warning: "warning",
	};

	const { setGroup, setGroupEdit, setGroups, playoff } = usePlayoffContext();

	// useEffect(() => {
	// 	const fetch = async () => {
	// 		let res = await axiosInstance.get("product-task/");
	// 		setTasks(res.data.results);
	// 	};
	// 	fetch();
	// }, []);

	useEffect(() => {
		if (open) {
			reset({ playoff: playoff?.title ?? "" });
		}
	}, [open, playoff?.title, reset]);

	useEffect(() => {
		tasksRef.current = tasks;
	}, [tasks]);

	useEffect(() => {
		if (!taskIds) {
			return;
		}

		let isCancelled = false;
		const tasksToPoll = tasksRef.current;

		const pollTasksSequentially = async () => {
			for (let i = 0; i < tasksToPoll.length; i++) {
				if (isCancelled) {
					break;
				}

				let currentTask = tasksToPoll[i];

				if (currentTask.progress >= 100) {
					continue;
				}

				while (!isCancelled && currentTask.progress < 100) {
					try {
						const res = await axiosInstance.get(
							`/product/task/${currentTask.id}`,
						);
						const updatedTask = res.data;
						if (!updatedTask) {
							break;
						}

						currentTask = updatedTask;

						// Update tasks state
						setTasks((prev) =>
							prev.map((t) =>
								t.id === updatedTask.id ? updatedTask : t,
							),
						);

						// Update products immediately when task finishes
						if (
							currentTask.progress >= 100 &&
							currentTask.product
						) {
							const finishedProduct = currentTask.product;
							setProducts((prevProducts) => {
								const merged = [
									...prevProducts,
									finishedProduct,
								];
								return setUniqueProducts(merged);
							});
						}
					} catch (err) {
						console.warn(
							`Failed to fetch task ${currentTask.id}:`,
							err,
						);
						await new Promise((r) => setTimeout(r, 10000));
					}

					if (currentTask.progress < 100) {
						await new Promise((r) => setTimeout(r, 10000));
					}
				}
			}
		};

		pollTasksSequentially();

		return () => {
			isCancelled = true;
		};
	}, [setProducts, setUniqueProducts, taskIds]);

	const onSubmit = async (data) => {
		try {
			const { products, product, ...rest } = group;
			data = { ...data, group: rest };

			setLoading(true);

			const res = await axiosInstance.post("get-product-sheet/", data);
			const response = res.data;

			console.log(response);

			if (response.success) {
				const { batch } = response;
				setTasks(batch.tasks);
				setHideForm(true);
				setGroup(batch.group);
				setGroups((prev) => [
					...prev,
					{ ...batch.group, products: [] },
				]);
				setGroupEdit(true);
			} else {
				showToast(response.error, "error", 5000);
			}
		} catch (err) {
			console.error("Error submitting form:", err);
			showToast("Something went wrong. Please try again.", "error", 5000);
		} finally {
			setLoading(false); // stop loading in all cases
		}
	};
	return (
		<Dialog
			slotProps={{
				paper: {
					sx: {
						minWidth: (theme) => theme.breakpoints.values.lg, // lock to md width
						// maxWidth: (theme) => theme.breakpoints.values.md, // optional: fix width
					},
				},
			}}
			open={open}
			maxWidth="sm"
			// onClose={toggle}
		>
			{loading && <FullHeightLoader />}
			<DialogContent>
				<Box m={{ lg: 2 }} sx={{ minWidth: 400 }}>
					<Box component={"form"} onSubmit={handleSubmit(onSubmit)}>
						<Box my={3}>
							<Controller
								name="playoff"
								control={control}
								rules={{
									required:
										"Please enter Playoff name for future reference",
								}}
								render={({ field, fieldState }) => (
									<TextField
										{...field}
										// type="number"
										label="Playoff Name"
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
										fullWidth
									/>
								)}
							/>
						</Box>
						<Box my={3}>
							<Controller
								name="sheet"
								control={control}
								rules={{
									required: "Please enter sheet url",
									validate: {
										// checkUrl: async (v) =>
										// 	(await checkProductExist(v)) ||
										// 	"Product already Exist",
										isURL: (value) =>
											validator.isURL(value) ||
											"Please enter a valid url",
										isGoogleSheet: (value) =>
											/https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+/.test(
												value,
											) ||
											"Please enter a valid Google Sheet URL",
									},
								}}
								render={({ field, fieldState }) => (
									<TextField
										{...field}
										// type="number"
										label="Sheet URL"
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
										fullWidth
									/>
								)}
							/>
							<Typography
								variant="body2"
								pt={2}
								component={"div"}
							>
								Please ensure that the sheet has read access for{" "}
								<Typography
									component="div"
									variant="body2"
									sx={{
										display: "flex",
										gap: 1,
										alignItems: "center",
										fontWeight: "bold",
										color: "primary.main",
									}}
								>
									firebase-adminsdk-83egg@grape-11d6f.iam.gserviceaccount.com
									<CopyToClipboard
										text={
											"firebase-adminsdk-83egg@grape-11d6f.iam.gserviceaccount.com"
										}
										onCopy={() =>
											showToast("copied", "success")
										}
									>
										<IconButton
											size="small"
											color="primary"
										>
											<ContentCopyRounded fontSize="6" />
										</IconButton>
									</CopyToClipboard>
								</Typography>
							</Typography>
						</Box>
						<Box my={3}>
							<Grid container spacing={2}>
								{tasks.map((task) => (
									<Grid
										item
										xs={12}
										sm={6}
										md={4}
										key={task.id}
									>
										<Card
											variant="outlined"
											sx={{
												height: "100%", // equal height
												display: "flex",
												flexDirection: "column",
											}}
										>
											<CardContent
												sx={{
													"&:last-child": {
														pb: 1,

														// Add your custom styles here
													},
													p: 1,
													flex: 1,
													display: "flex",
													flexDirection: "column",
													justifyContent:
														"space-between",
												}}
											>
												<Box
													sx={{
														position: "relative",
														width: "100%",
														aspectRatio: "3/4", // works in modern browsers
														borderRadius: 3,
														overflow: "hidden",
														// mt: 2,
													}}
												>
													<StatusAvatar
														status={task.status}
														src={
															task.product?.image
																?.file
														}
													/>
													{/* <Avatar
														variant="square"
														src={
															task.product?.image
																?.file
														}
														sx={{
															position:
																"absolute",
															top: 0,
															left: 0,
															width: "100%",
															height: "100%",
															objectFit: "cover", // or "fill"
														}}
													/> */}
												</Box>
												{/* Bottom Section */}
												<Box pt={2}>
													<Typography
														variant="body2"
														sx={{
															color:
																statusColorMap[
																	task.status
																] || "inhert",
															textTransform:
																"capitalize",
														}}
													>
														{` ${task.status}`}
													</Typography>

													{task.status ===
														"failed" && (
														<Typography
															variant="body2"
															color="secondary"
														>
															{task.error_message}
														</Typography>
													)}

													<Box
														sx={{
															display: "flex",
															alignItems:
																"center",
														}}
													>
														<Box
															sx={{
																width: "100%",
																mr: 1,
															}}
														>
															<LinearProgress
																variant="determinate"
																color={
																	statusColorMap[
																		task
																			.status
																	] ||
																	"primary"
																}
																value={
																	task.progress
																}
															/>
														</Box>
														<Box
															sx={{
																minWidth: 35,
															}}
														>
															<Typography
																variant="body2"
																sx={{
																	color: "text.secondary",
																}}
															>
																{`${Math.round(
																	task.progress,
																)}%`}
															</Typography>
														</Box>
													</Box>
												</Box>
											</CardContent>
										</Card>
									</Grid>
								))}
							</Grid>
						</Box>
						<Box
							my={3}
							sx={{
								display: "flex",
								gap: 2,
								justifyContent: "flex-end",
							}}
						>
							<Button
								onClick={toggle}
								color="secondary"
								disabled={
									tasks.filter((t) => t.progress < 100).length
								}
								variant="contained"
							>
								{tasks.filter((t) => t.progress < 100)
									.length ? (
									<CircularProgress />
								) : (
									"Close"
								)}
							</Button>
							{!hideForm && (
								<Button
									type="submit"
									disabled={hideForm}
									// onClick={handleSave}
									color="primary"
									variant="contained"
								>
									Submit
								</Button>
							)}
						</Box>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};
